
// Fetch IPv4 and IPv6 separately to handle dual-stack matching with game servers
// (game servers typically see IPv4 while browsers may default to IPv6)
const fetchIP = async (url: string): Promise<string> => {
  try {
    const res = await fetch(url);
    const data = await res.json();
    return data.ip || '';
  } catch {
    return '';
  }
};

export interface DualStackIPs {
  primary: string;     // Whatever the browser uses by default (matches user's outgoing IP)
  ipv4: string;        // Forced IPv4
  ipv6: string;        // Forced IPv6
}

export const getDualStackIPs = async (): Promise<DualStackIPs> => {
  const [primary, ipv4, ipv6] = await Promise.all([
    fetchIP('https://api.ipify.org?format=json'),
    fetchIP('https://api4.ipify.org?format=json'),
    fetchIP('https://api6.ipify.org?format=json'),
  ]);
  return { primary, ipv4, ipv6 };
};

// Backward compat: return primary IP
export const getRealIP = async (): Promise<string> => {
  const ips = await getDualStackIPs();
  // Prefer IPv4 when available since game servers usually see IPv4
  return ips.ipv4 || ips.primary || ips.ipv6 || '';
};

export const hashString = async (input: string): Promise<string> => {
  const encoder = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const hashIPWithDate = async (ip: string): Promise<string> => {
  return hashString(ip + new Date().toDateString());
};

// Legacy API
export const hashIP = async (_ip?: string): Promise<string> => {
  try {
    const userIP = await getRealIP();
    return await hashIPWithDate(userIP);
  } catch {
    return hashString(navigator.userAgent + new Date().toDateString());
  }
};

export const generateIPHash = hashIP;
